import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Empty } from '@/components/Empty';
import { Input } from '@/components/Input';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Spinner } from '@/components/Spinner';
import { sb, useSupabaseQuery } from '@/hooks/useSupabaseQuery';
import { resolveEtaScenarios, isScenarioExecutable, computeMinRequiredCapital } from '@/lib/etaScenarios';
import { fmtDateTime } from '@/lib/format';
import { useAuth } from '@/providers/AuthProvider';
import { useHaptic } from '@/hooks/useHaptic';
import type { EtaAlert, OptionBalance, ScenarioResult, ThetaDecision, ThetaPosition, ThetaTrade, UserSettings } from '@/types/domain';

type TabId = 'overview' | 'candidates' | 'positions' | 'history' | 'settings' | 'balances' | 'kill';

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'candidates', label: 'Кандидаты' },
  { id: 'positions', label: 'Позиции' },
  { id: 'history', label: 'История' },
  { id: 'settings', label: 'Настройки' },
  { id: 'balances', label: 'Балансы' },
  { id: 'kill', label: 'Kill' },
];

interface ThetaCandidateRow {
  alert: EtaAlert;
  scenario: ScenarioResult;
  scenarioLabel: 'Без шорта' | 'Со шортом';
  minRequiredUsd: number;
  profitPer100Usd: number;
  perExchangeRequirement: Record<string, number>;
  fitsBalance: boolean;
}

type ThetaSettings = Pick<UserSettings,
  | 'theta_balance_usd' | 'theta_autotrade_enabled' | 'theta_kill_switch'
  | 'theta_leverage_btc' | 'theta_leverage_eth' | 'theta_leverage_default'
  | 'theta_hedge_enabled' | 'theta_max_open_positions' | 'theta_max_position_usd'
  | 'theta_max_total_exposure_usd' | 'theta_min_hours_to_expiry' | 'theta_max_per_expiry'
  | 'theta_max_requote_drift_pct'>;

const DEFAULT_SETTINGS: ThetaSettings = {
  theta_balance_usd: 0,
  theta_autotrade_enabled: false,
  theta_kill_switch: false,
  theta_leverage_btc: 5,
  theta_leverage_eth: 3,
  theta_leverage_default: 2,
  theta_hedge_enabled: false,
  theta_max_open_positions: 5,
  theta_max_position_usd: 0,
  theta_max_total_exposure_usd: 0,
  theta_min_hours_to_expiry: 12,
  theta_max_per_expiry: 2,
  theta_max_requote_drift_pct: 5,
};

const SETTINGS_COLUMNS = Object.keys(DEFAULT_SETTINGS).join(', ');

/** Понятные причины решений бота (theta_decisions.reason). */
const DECISION_LABELS: Record<string, string> = {
  selected: 'Открыта',
  skipped_insufficient_balance: 'Не хватает баланса',
  skipped_kill_switch: 'Kill switch',
  skipped_autotrade_off: 'Автотрейд выключен',
  no_fill_5s: 'Нет исполнимой глубины',
  price_drift_5pct: 'Цена ушла',
  skipped_risk_limit: 'Лимит риска',
  skipped_live_quote: 'Нет живого стакана',
  execution_failed: 'Не исполнилась',
  execution_partial: 'Частичное исполнение',
};

function marginColor(ratio: number | null | undefined): string | undefined {
  if (ratio == null) return undefined;
  if (ratio >= 1) return 'var(--tg-destructive)';
  if (ratio >= 0.8) return '#ff9500';
  return '#34c759';
}

function fmtUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${v.toFixed(2)}`;
}

// Автотрейд (theta-executor) открывает только «Со шортом»: сценарий «Без шорта» предполагает
// уже существующий лонг. Кандидаты в UI показываются по тому же правилу.
function pickExecutableScenario(withShort: ScenarioResult | null): { scenario: ScenarioResult; label: 'Со шортом' } | null {
  if (!isScenarioExecutable(withShort) || withShort!.buyExch === withShort!.sellExch) return null;
  return { scenario: withShort!, label: 'Со шортом' };
}

function TabBar({ active, onChange }: { active: TabId; onChange: (id: TabId) => void }): JSX.Element {
  return (
    <div style={{ display: 'flex', overflowX: 'auto', margin: '0 var(--space-md) var(--space-md)', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--tg-separator)' }}>
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          style={{
            flex: '0 0 auto',
            minWidth: 72,
            minHeight: 36,
            border: 'none',
            background: active === t.id ? 'var(--tg-button)' : 'transparent',
            color: active === t.id ? 'var(--tg-button-text)' : 'var(--tg-text)',
            fontSize: 11,
            cursor: 'pointer',
            fontWeight: active === t.id ? 600 : 400,
            padding: '0 8px',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function ThetaPage(): JSX.Element {
  const { userId } = useAuth();
  const { notify } = useHaptic();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabId>('overview');
  const [statusFilter, setStatusFilter] = useState('all');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const alertsQ = useSupabaseQuery<EtaAlert[]>(
    () => sb.from('eta_alerts').select('*').order('created_at', { ascending: false }).limit(200),
    [userId],
    30_000,
  );

  const balancesQ = useSupabaseQuery<OptionBalance[]>(
    () => sb.from('latest_option_balances').select('*').order('exchange', { ascending: true }),
    [userId],
    30_000,
  );

  const positionsQ = useSupabaseQuery<ThetaPosition[]>(
    () => sb.from('theta_positions').select('*').order('opened_at', { ascending: false }).limit(100),
    [userId],
    15_000,
  );

  const decisionsQ = useSupabaseQuery<ThetaDecision[]>(
    () => sb.from('theta_decisions').select('*').not('eta_alert_id', 'is', null).order('created_at', { ascending: false }).limit(300),
    [userId],
    30_000,
  );

  const tradesQ = useSupabaseQuery<ThetaTrade[]>(
    () => sb.from('theta_trades').select('*').order('opened_at', { ascending: false }).limit(100),
    [userId],
    30_000,
  );

  useEffect(() => {
    if (!userId) return;
    void sb
      .from('user_settings')
      .select(SETTINGS_COLUMNS)
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        const v = (data ?? {}) as Partial<ThetaSettings>;
        const next = { ...DEFAULT_SETTINGS };
        for (const key of Object.keys(DEFAULT_SETTINGS) as Array<keyof ThetaSettings>) {
          if (v[key] != null) (next as Record<string, unknown>)[key] = v[key];
        }
        setSettings(next);
        setSettingsLoaded(true);
      });
  }, [userId]);

  const saveSettingsDebounced = useCallback((next: typeof settings): void => {
    if (!userId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void sb.from('user_settings').upsert(
        {
          user_id: userId,
          ...next,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id' },
      ).then(({ error }) => {
        if (error) notify('error');
      });
    }, 300);
  }, [notify, userId]);

  function updateSettings(patch: Partial<typeof settings>): void {
    const next = { ...settings, ...patch };
    setSettings(next);
    saveSettingsDebounced(next);
  }

  const freeBalances = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of balancesQ.data ?? []) {
      if (!row.error) map[row.exchange] = Number(row.free_usdt ?? 0);
    }
    return map;
  }, [balancesQ.data]);

  const totalRealBalance = useMemo(
    () => (balancesQ.data ?? []).filter((b) => !b.error).reduce((sum, b) => sum + Number(b.free_usdt ?? 0), 0),
    [balancesQ.data],
  );

  const effectiveBalance = settings.theta_balance_usd > 0 ? settings.theta_balance_usd : totalRealBalance;

  const candidateRows = useMemo((): ThetaCandidateRow[] => {
    const rows: ThetaCandidateRow[] = [];
    for (const alert of alertsQ.data ?? []) {
      const resolved = resolveEtaScenarios(alert);
      const picked = pickExecutableScenario(resolved.withShort);
      if (!picked || picked.scenario.netUsd <= 0) continue;
      const min = computeMinRequiredCapital(
        picked.scenario,
        {
          btc: settings.theta_leverage_btc,
          eth: settings.theta_leverage_eth,
          default: settings.theta_leverage_default,
        },
        freeBalances,
        alert.underlying,
        settings.theta_hedge_enabled,
      );
      const enriched: ScenarioResult = {
        ...picked.scenario,
        minRequiredUsd: min.minRequiredUsd,
        perExchangeRequirement: min.perExchangeRequirement,
        hedgeRequirement: min.hedgeRequirement,
        profitPer100Usd: min.profitPer100Usd,
        fitsBalance: min.fitsBalance,
      };
      rows.push({
        alert,
        scenario: enriched,
        scenarioLabel: picked.label,
        minRequiredUsd: min.minRequiredUsd,
        perExchangeRequirement: min.perExchangeRequirement,
        profitPer100Usd: min.profitPer100Usd,
        fitsBalance: min.fitsBalance,
      });
    }
    return rows.sort((a, b) => b.profitPer100Usd - a.profitPer100Usd);
  }, [alertsQ.data, freeBalances, settings.theta_leverage_btc, settings.theta_leverage_default, settings.theta_leverage_eth, settings.theta_hedge_enabled]);

  // Последнее решение бота по каждому алерту — почему кандидат не был открыт.
  const decisionByAlert = useMemo(() => {
    const map = new Map<number, ThetaDecision>();
    for (const d of decisionsQ.data ?? []) {
      if (d.eta_alert_id != null && !map.has(d.eta_alert_id)) map.set(d.eta_alert_id, d);
    }
    return map;
  }, [decisionsQ.data]);

  // Прямой update позиции пользователю запрещён RLS: только RPC, которая умеет open → close_requested.
  async function closeNow(position: ThetaPosition): Promise<void> {
    const { error } = await sb.rpc('theta_request_close', { p_position_id: position.id });
    if (error) notify('error');
    else {
      notify('success');
      positionsQ.refetch();
    }
  }

  async function activateKillSwitch(): Promise<void> {
    if (!userId) return;
    const { error } = await sb.from('user_settings').upsert(
      {
        user_id: userId,
        theta_kill_switch: true,
        theta_autotrade_enabled: false,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' },
    );
    if (error) {
      notify('error');
      return;
    }
    setSettings((s) => ({ ...s, theta_kill_switch: true, theta_autotrade_enabled: false }));
    // Сразу запрашиваем закрытие открытых позиций (раньше это происходило только при следующем
    // запуске theta-executor, т.е. когда появятся новые Eta-алерты).
    const open = (positionsQ.data ?? []).filter((p) => p.status === 'open');
    await Promise.all(open.map((p) => sb.rpc('theta_request_close', { p_position_id: p.id })));
    positionsQ.refetch();
    notify('success');
  }

  async function deactivateKillSwitch(): Promise<void> {
    if (!userId) return;
    const { error } = await sb.from('user_settings').upsert(
      { user_id: userId, theta_kill_switch: false, updated_at: new Date().toISOString() },
      { onConflict: 'user_id' },
    );
    if (error) {
      notify('error');
      return;
    }
    // Автотрейд остаётся выключенным — включается отдельно и осознанно.
    setSettings((s) => ({ ...s, theta_kill_switch: false }));
    notify('success');
  }

  if (!userId) return <Empty title="Нужна авторизация" hint="Откройте приложение через Telegram." />;

  const openPositions = (positionsQ.data ?? []).filter((p) => p.status === 'open' || p.status === 'close_requested');
  const filteredTrades = (tradesQ.data ?? []).filter((t) => statusFilter === 'all' || t.status === statusFilter);

  return (
    <Page title="Theta — авто-бот">
      <TabBar active={tab} onChange={setTab} />

      {tab === 'overview' && (
        <>
          <Section header="О функции" footer="При выключенном автоисполнении Theta продолжает показывать кандидаты и Eta-уведомления, но ордера не ставит.">
            <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <b>Автоисполнение</b>
                <input
                  type="checkbox"
                  checked={settings.theta_autotrade_enabled}
                  onChange={(e) => updateSettings({ theta_autotrade_enabled: e.target.checked })}
                  disabled={settings.theta_kill_switch}
                />
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--tg-hint)' }}>
                {settings.theta_kill_switch ? 'Kill-switch активен: новые позиции не открываются.' : settings.theta_autotrade_enabled ? 'Включено: бот реагирует на свежие Eta-отчёты.' : 'Выключено: только аналитика и уведомления.'}
              </div>
            </div>
          </Section>

          <Section header="Баланс для активации" footer="Manual override: если значение > 0, используется вместо суммы биржевых балансов.">
            {!settingsLoaded ? <Spinner /> : (
              <>
                <Input
                  label="theta_balance_usd (manual override)"
                  type="number"
                  min="0"
                  step="0.01"
                  value={String(settings.theta_balance_usd)}
                  onChange={(e) => updateSettings({ theta_balance_usd: Number(e.target.value || 0) })}
                />
                <div style={{ padding: '8px var(--space-lg)', fontSize: 12, color: 'var(--tg-hint)' }}>
                  Реальный суммарный free USDT: <b>{fmtUsd(totalRealBalance)}</b><br />
                  Эффективный баланс для расчёта: <b>{fmtUsd(effectiveBalance)}</b>
                </div>
              </>
            )}
          </Section>

          <Section header="Сводка">
            <div style={{ padding: 'var(--space-md) var(--space-lg)', display: 'grid', gap: 8, fontSize: 13 }}>
              <div>Кандидатов с Net {'>'} 0: <b>{candidateRows.length}</b></div>
              <div>Открытых позиций: <b>{openPositions.length}</b></div>
              <div>Kill-switch: <b style={{ color: settings.theta_kill_switch ? 'var(--tg-destructive)' : '#34c759' }}>{settings.theta_kill_switch ? 'ON' : 'OFF'}</b></div>
            </div>
          </Section>
        </>
      )}

      {tab === 'candidates' && (
        <Section header="Theta-кандидаты" footer="Сортировка по Profit / $100 (desc) — так же выбирает бот. Fit проверяет баланс по каждой бирже отдельно. «Бот» — последнее решение автотрейда по алерту.">
          {alertsQ.isLoading ? <Spinner /> : candidateRows.length === 0 ? (
            <Empty icon="θ" title="Нет подходящих кандидатов" hint="Нужны прибыльные и исполняемые сценарии." />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--tg-separator)', color: 'var(--tg-hint)' }}>
                    {['Время', 'Опцион', 'Net', 'Min capital', 'Profit / $100', 'Сценарий', 'Fit', 'Бот', 'Биржи/требования'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {candidateRows.map((row) => (
                    <tr key={row.alert.id} style={{ borderBottom: '0.5px solid var(--tg-separator)', cursor: 'pointer' }} onClick={() => navigate(`/eta/${row.alert.id}`)}>
                      <td style={{ padding: '6px 8px', color: 'var(--tg-hint)', whiteSpace: 'nowrap' }}>{fmtDateTime(row.alert.created_at)}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.alert.normalized_key}</td>
                      <td style={{ padding: '6px 8px', color: '#34c759', fontWeight: 700 }}>{fmtUsd(row.scenario.netUsd)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtUsd(row.minRequiredUsd)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>{row.profitPer100Usd.toFixed(2)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{row.scenarioLabel}</td>
                      <td style={{ padding: '6px 8px', color: row.fitsBalance ? '#34c759' : 'var(--tg-destructive)', fontWeight: 600 }}>{row.fitsBalance ? 'YES' : 'NO'}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }} title={decisionByAlert.get(row.alert.id)?.details ?? ''}>
                        {(() => {
                          const d = decisionByAlert.get(row.alert.id);
                          if (!d) return <span style={{ color: 'var(--tg-hint)' }}>—</span>;
                          return (
                            <span style={{ color: d.reason === 'selected' ? '#34c759' : 'var(--tg-hint)' }}>
                              {DECISION_LABELS[d.reason] ?? d.reason}
                              {d.details && d.reason !== 'selected' ? <span style={{ display: 'block', fontSize: 10, maxWidth: 220, whiteSpace: 'normal' }}>{d.details}</span> : null}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ padding: '6px 8px', color: 'var(--tg-hint)', whiteSpace: 'nowrap' }}>
                        {Object.entries(row.perExchangeRequirement).map(([exchange, usd]) => `${exchange}:${usd.toFixed(2)}`).join(' • ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {tab === 'positions' && (
        <Section header="Открытые позиции Theta" footer="Зелёным — закрытие сейчас выгоднее удержания до экспирации. Маржа шорта: при 80% бот предупреждает, при 100% закрывает позицию.">
          {positionsQ.isLoading ? <Spinner /> : openPositions.length === 0 ? <Empty title="Нет открытых позиций" /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--tg-separator)', color: 'var(--tg-hint)' }}>
                    {['Открыта', 'Опцион', 'Сценарий', 'Биржи', 'plan_net_usd', 'current_net_usd', 'Маржа шорта', 'Экспирация', 'Статус', ''].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {openPositions.map((p) => {
                    const current = p.current_net_usd ?? p.planned_net_usd ?? 0;
                    const planned = p.planned_net_usd ?? 0;
                    const canClose = current >= planned && planned > 0;
                    return (
                      <tr key={p.id} style={{ borderBottom: '0.5px solid var(--tg-separator)', background: canClose ? 'rgba(52,199,89,0.12)' : 'transparent' }}>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtDateTime(p.opened_at)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{p.symbol}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{p.scenario}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{[p.buy_exchange, p.sell_exchange, p.hedge_exchange].filter(Boolean).join(' / ') || '—'}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtUsd(planned)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtUsd(current)}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600, color: marginColor(p.margin_ratio) }} title={p.margin_locked_usd != null ? `Заблокировано ${fmtUsd(p.margin_locked_usd)}` : ''}>
                          {p.margin_ratio != null ? `${(p.margin_ratio * 100).toFixed(0)}%` : '—'}
                        </td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{p.expiry_at ? fmtDateTime(p.expiry_at) : '—'}</td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          {p.status}
                          {p.quotes_source ? <span style={{ display: 'block', fontSize: 10, color: 'var(--tg-hint)' }}>{p.quotes_source === 'live' ? 'живой стакан' : 'снимок zeta'}</span> : null}
                        </td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          <Button variant="secondary" disabled={p.status !== 'open'} onClick={() => void closeNow(p)}>Закрыть сейчас</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {tab === 'history' && (
        <Section header="История сделок" footer="Показываются последние 100 записей theta_trades.">
          <label style={{ display: 'block', padding: '8px var(--space-lg)' }}>
            <span style={{ fontSize: 12, color: 'var(--tg-hint)' }}>Фильтр статуса</span>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: '100%', marginTop: 6 }}>
              <option value="all">Все</option>
              <option value="open">open</option>
              <option value="closed_profit">closed_profit</option>
              <option value="closed_loss">closed_loss</option>
              <option value="closed_expired">closed_expired</option>
              <option value="failed">failed</option>
              <option value="partial">partial</option>
            </select>
          </label>
          {tradesQ.isLoading ? <Spinner /> : filteredTrades.length === 0 ? <Empty title="История пуста" /> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--tg-separator)', color: 'var(--tg-hint)' }}>
                    {['Время', 'Опцион', 'Сценарий', 'Биржи', 'planned_net', 'realized_net', 'Статус', 'Заметки'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTrades.map((t) => (
                    <tr key={t.id} style={{ borderBottom: '0.5px solid var(--tg-separator)' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtDateTime(t.opened_at)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{t.symbol}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{t.scenario}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{[t.buy_exchange, t.sell_exchange, t.hedge_exchange].filter(Boolean).join(' / ') || '—'}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtUsd(t.planned_net_usd)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtUsd(t.realized_net_usd)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: t.status === 'partial' ? 'var(--tg-destructive)' : undefined }}>{t.status}</td>
                      <td style={{ padding: '6px 8px', color: 'var(--tg-hint)', fontSize: 11 }}>{t.notes ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}

      {tab === 'settings' && (
        <>
        <Section header="Лимиты риска" footer="0 — без лимита. Бот проверяет лимиты до живой проверки цены. Сохраняется автоматически.">
          <Input label="Макс. открытых позиций" type="number" min="0" step="1" value={String(settings.theta_max_open_positions)} onChange={(e) => updateSettings({ theta_max_open_positions: Number(e.target.value || 0) })} />
          <Input label="Макс. капитал на сделку, $" type="number" min="0" step="1" value={String(settings.theta_max_position_usd)} onChange={(e) => updateSettings({ theta_max_position_usd: Number(e.target.value || 0) })} />
          <Input label="Макс. капитал во всех сделках, $" type="number" min="0" step="1" value={String(settings.theta_max_total_exposure_usd)} onChange={(e) => updateSettings({ theta_max_total_exposure_usd: Number(e.target.value || 0) })} />
          <Input label="Не входить ближе N часов к экспирации" type="number" min="0" step="1" value={String(settings.theta_min_hours_to_expiry)} onChange={(e) => updateSettings({ theta_min_hours_to_expiry: Number(e.target.value || 0) })} />
          <Input label="Макс. позиций на одну экспирацию" type="number" min="0" step="1" value={String(settings.theta_max_per_expiry)} onChange={(e) => updateSettings({ theta_max_per_expiry: Number(e.target.value || 0) })} />
          <Input label="Допустимый уход цены перед сделкой, %" type="number" min="0.1" step="0.5" value={String(settings.theta_max_requote_drift_pct)} onChange={(e) => updateSettings({ theta_max_requote_drift_pct: Number(e.target.value || 5) })} />
        </Section>
        <Section header="Хедж" footer="Лонг и шорт одного и того же опциона дают нулевую дельту — хедж фьючерсом обычно не нужен и только замораживает капитал на WEEX.">
          <div style={{ padding: '8px var(--space-lg)' }}>
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Хеджировать на WEEX</span>
              <input type="checkbox" checked={settings.theta_hedge_enabled} onChange={(e) => updateSettings({ theta_hedge_enabled: e.target.checked })} />
            </label>
          </div>
        </Section>
        <Section header="Плечи для хеджа" footer="Используются только при включённом хедже.">
          <Input label="BTC leverage" type="number" min="1" step="1" value={String(settings.theta_leverage_btc)} onChange={(e) => updateSettings({ theta_leverage_btc: Number(e.target.value || 1) })} />
          <Input label="ETH leverage" type="number" min="1" step="1" value={String(settings.theta_leverage_eth)} onChange={(e) => updateSettings({ theta_leverage_eth: Number(e.target.value || 1) })} />
          <Input label="Default leverage" type="number" min="1" step="1" value={String(settings.theta_leverage_default)} onChange={(e) => updateSettings({ theta_leverage_default: Number(e.target.value || 1) })} />
          <div style={{ padding: '8px var(--space-lg)' }}>
            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Автоисполнение</span>
              <input type="checkbox" checked={settings.theta_autotrade_enabled} disabled={settings.theta_kill_switch} onChange={(e) => updateSettings({ theta_autotrade_enabled: e.target.checked })} />
            </label>
          </div>
        </Section>
        </>
      )}

      {tab === 'balances' && (
        <Section header="Балансы по биржам" footer="Суммарный free USDT по всем биржам; строки с ошибкой подсвечены серым.">
          <div style={{ padding: 'var(--space-md) var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)' }}>
            <div style={{ fontSize: 12, color: 'var(--tg-hint)' }}>Итого free USDT</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{fmtUsd(totalRealBalance)}</div>
          </div>
          {balancesQ.isLoading ? <Spinner /> : (balancesQ.data ?? []).length === 0 ? <Empty title="Балансов пока нет" /> : (
            <div>
              {(balancesQ.data ?? []).map((b) => (
                <div key={b.exchange} title={b.error ?? ''} style={{ padding: '8px var(--space-lg)', borderBottom: '0.5px solid var(--tg-separator)', color: b.error ? 'var(--tg-hint)' : 'var(--tg-text)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <b>{b.exchange.toUpperCase()}</b>
                    <span>{fmtUsd(Number(b.free_usdt ?? 0))}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tg-hint)' }}>{fmtDateTime(b.taken_at)}{b.error ? ` • ${b.error}` : ''}</div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === 'kill' && (
        <Section header="Kill Switch" footer="При активации отключает автоисполнение и сразу переводит открытые позиции в закрытие. После выключения автоисполнение нужно включить заново вручную.">
          <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
            <Button
              variant="primary"
              style={{ width: '100%', background: 'var(--tg-destructive)', color: '#fff' }}
              onClick={() => void activateKillSwitch()}
              disabled={settings.theta_kill_switch}
            >
              {settings.theta_kill_switch ? '🛑 Kill Switch ON' : '🛑 Kill Switch'}
            </Button>
            {settings.theta_kill_switch && (
              <Button variant="secondary" style={{ width: '100%', marginTop: 8 }} onClick={() => void deactivateKillSwitch()}>
                Выключить Kill Switch
              </Button>
            )}
          </div>
        </Section>
      )}
    </Page>
  );
}
